import type { ReactNode } from "react";
import clsx from "clsx";
import { storyblokEditable } from "@storyblok/react";
import {
  NODE_HEADING,
  NODE_PARAGRAPH,
  render,
} from "storyblok-rich-text-react-renderer";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { sbBlok } from "@/types/sb-types";
import { formatArticleDate } from "@/utils/articles";

interface ArticleIntroProps {
  blok: sbBlok;
  publishedAt?: string;
  readingMinutes?: number | null;
}

const ArticleIntro = ({
  blok,
  publishedAt,
  readingMinutes,
}: ArticleIntroProps) => {
  const date = formatArticleDate(blok.date_override || publishedAt);

  return (
    <div
      className={clsx("grid-container", {
        "pt-7 lg:pt-14": blok.spacing_top?.[0]?.option === "large",
        "pt-14 lg:pt-30": blok.spacing_top?.[0]?.option === "x_large",
        "pt-30 lg:pt-45": blok.spacing_top?.[0]?.option === "xx_large",
        "pb-7 lg:pb-14": blok.spacing_bottom?.[0]?.option === "large",
        "pb-14 lg:pb-30": blok.spacing_bottom?.[0]?.option === "x_large",
        "pb-30 lg:pb-45": blok.spacing_bottom?.[0]?.option === "xx_large",
      })}
    >
      <div className="col-start-1 col-span-4 row-start-1 lg:col-start-3 lg:col-span-12 2xl:col-start-9 2xl:col-span-16">
        <div {...storyblokEditable(blok)} className="flex flex-col gap-12.5">
          <div className="flex flex-col gap-7.5">
            {render(blok.title, {
              nodeResolvers: {
                [NODE_HEADING]: (
                  children: ReactNode,
                  props: { level?: 1 | 2 | 3 | 4 | 5 | 6 },
                ) => {
                  const HEADING_TAGS = { 1: "h1", 2: "h2", 3: "h3" } as const;
                  const Tag =
                    HEADING_TAGS[(props?.level as 1 | 2 | 3) ?? 1] ?? "h3";
                  return (
                    <Tag className="m-0 text-4xl! xl:text-5xl!">{children}</Tag>
                  );
                },
              },
            })}
            {render(blok.subtitle, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children: ReactNode) => (
                  <p className="m-0 text-xl xl:text-2xl">{children}</p>
                ),
              },
            })}
          </div>

          {(date || readingMinutes) && (
            <div className="flex gap-7.5 text-xs text-tm-c-gray-300">
              {date && (
                <span className="flex items-center gap-1">
                  <CalendarMonthOutlinedIcon fontSize="small" />
                  {date}
                </span>
              )}
              {readingMinutes && (
                <span className="flex items-center gap-1">
                  <ScheduleOutlinedIcon fontSize="small" />
                  {readingMinutes} min read
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleIntro;
